import type { SegmentRule } from "@domains/segments/shared/segmentsTypes.ts";
import { Button } from "@/components/ui/button.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useSegmentsClient } from "@domains/segments/client/api/segmentsQuery.ts";
import { segmentsI18NextInstanceKey } from "@domains/segments/client/i18n/i18next.ts";
import { SegmentMemberPreview } from "@domains/segments/client/components/preview/SegmentMemberPreview.tsx";
import { SegmentRules } from "@domains/segments/client/components/rules/SegmentRules.tsx";
import { ConfirmDialog } from "@packages/core-client/forms/ConfirmDialog.tsx";
import { useSkeletonState } from "@packages/core-client/hooks/useSkeletonState.ts";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { ErrorState } from "@packages/core-client/errors/ErrorState.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { setTeamAdHocFilter, TEAM_FILTER_MATCH_TYPE, useTeamAdHocDraft, useTeamAdHocFilter } from "../../hooks/teamAdHocFilter.ts";
import { useTeamSegment } from "../../hooks/useTeamSegment.ts";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { SaveAsSegmentDialog } from "../SaveAsSegmentDialog.tsx";
import type { TeamSegmentFormHandle } from "./TeamSegmentForm.tsx";

const PLACEHOLDER_FIELDS = { items: [] };

export interface CurrentFiltersFormProps {
    onApplied: () => void;
}

function noop() {}

export const CurrentFiltersForm = forwardRef<TeamSegmentFormHandle, CurrentFiltersFormProps>(function CurrentFiltersForm({ onApplied }, ref) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("CurrentFiltersForm", { i18n: i18nextInstance });
    const segmentsI18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t: tSegmentForm } = useTranslation("SegmentForm", { i18n: segmentsI18nextInstance });
    const session = useSession();
    const { queryUtils } = useSegmentsClient();
    const [, setSegmentId] = useTeamSegment();

    const appliedFilter = useTeamAdHocFilter();
    const appliedDraft = useTeamAdHocDraft();

    const [rules, setRules] = useState<SegmentRule[]>(appliedDraft?.rules ?? []);
    const [hasIncompleteRule, setHasIncompleteRule] = useState(false);
    const [hasAttemptedApply, setHasAttemptedApply] = useState(false);
    const [isSavingAsSegment, setIsSavingAsSegment] = useState(false);

    const lockRules = useMemo(() => (appliedFilter && appliedDraft ? appliedFilter.rules.slice(0, appliedFilter.rules.length - appliedDraft.rules.length) : []), [appliedFilter, appliedDraft]);
    const combinedRules = useMemo(() => [...lockRules, ...rules], [lockRules, rules]);

    const orgId = session?.organizationId ?? "";
    const {
        data: employeeFields,
        isPending: areFieldsPending,
        isError: haveFieldsFailed,
        refetch: refetchFields
    } = useQuery(
        createQueryOptions(queryUtils.listEmployeeFields.queryOptions({ input: { orgId } }), {
            throwOnError: false
        })
    );
    const { isLoading: areFieldsLoading, hasContent: hasFieldsContent } = useSkeletonState({ isPending: areFieldsPending, data: employeeFields ? [employeeFields] : undefined, placeholder: [PLACEHOLDER_FIELDS] });
    const handleRetryFields = useCallback(() => void refetchFields(), [refetchFields]);

    const isDirty = !!appliedDraft && (rules.length !== appliedDraft.rules.length || rules.some((rule, index) => rule !== appliedDraft.rules[index]));

    const [pendingLeave, setPendingLeave] = useState<(() => void) | undefined>(undefined);

    useImperativeHandle(ref, () => ({ requestLeave: proceed => (isDirty ? setPendingLeave(() => proceed) : proceed()) }), [isDirty]);

    const handleLeave = useCallback(() => {
        pendingLeave?.();
        setPendingLeave(undefined);
    }, [pendingLeave]);
    const handleStay = useCallback(() => setPendingLeave(undefined), []);

    const isActionDisabled = rules.length === 0 || hasIncompleteRule;

    const handleApply = useCallback(() => {
        setHasAttemptedApply(true);

        if (isActionDisabled) {
            return;
        }

        setTeamAdHocFilter({ filter: { matchType: TEAM_FILTER_MATCH_TYPE, rules: combinedRules }, draft: { matchType: TEAM_FILTER_MATCH_TYPE, rules } });
        onApplied();
    }, [isActionDisabled, combinedRules, rules, onApplied]);

    const handleRequestSaveAsSegment = useCallback(() => setIsSavingAsSegment(true), []);
    const handleBackFromSave = useCallback(() => setIsSavingAsSegment(false), []);

    const handleSaved = useCallback(
        (newSegmentId: string) => {
            void setSegmentId(newSegmentId);
            setTeamAdHocFilter(null);
            onApplied();
        },
        [setSegmentId, onApplied]
    );

    if (!appliedFilter || !appliedDraft) {
        return null;
    }

    if (isSavingAsSegment) {
        return <SaveAsSegmentDialog matchType={TEAM_FILTER_MATCH_TYPE} rules={combinedRules} onBack={handleBackFromSave} onSaved={handleSaved} onIsSavingChange={noop} />;
    }

    return (
        <div className="flex h-full flex-col">
            <div className="bg-overlay sticky top-0 z-10 px-6 pt-6 pb-5">
                <p className="text-fg truncate text-base font-semibold">{t("title")}</p>
            </div>

            <div className="flex flex-col gap-5 px-6 pb-6">
                <output className="sr-only">{areFieldsLoading && tSegmentForm("rules.loading")}</output>

                {haveFieldsFailed ? (
                    <ErrorState isEmbedded kind="load" title={tSegmentForm("rules.error")} onRetry={handleRetryFields} retryLabel={tSegmentForm("rules.retry")} />
                ) : !hasFieldsContent ? null : areFieldsLoading || !employeeFields ? (
                    <Skeleton isLoading={areFieldsLoading} inert={areFieldsLoading} className="flex flex-col gap-2">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                                <div className="h-4 w-12" />
                                <div className="h-9 w-24" />
                                <div className="h-4 w-28" />
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row">
                                <div className="h-9 w-full sm:w-44" />
                                <div className="h-9 w-full sm:w-40" />
                                <div className="h-9 w-full sm:flex-1" />
                            </div>
                        </div>
                    </Skeleton>
                ) : (
                    <SegmentRules
                        fields={employeeFields.items}
                        rules={rules}
                        hasRequiredRules={false}
                        hasUnlistedRules={false}
                        hasUnlistedRequiredRules={false}
                        lockedRules={lockRules}
                        matchType={TEAM_FILTER_MATCH_TYPE}
                        isMatchTypeFixed
                        matchTypeFixedReason={t("matchTypeFixed")}
                        isOwnedByViewer
                        shouldShowErrors={hasAttemptedApply}
                        onRulesChange={setRules}
                        onValidityChange={setHasIncompleteRule}
                    >
                        {combinedRules.length > 0 && <SegmentMemberPreview matchType={TEAM_FILTER_MATCH_TYPE} rules={combinedRules} />}
                    </SegmentRules>
                )}
            </div>

            <div className="bg-overlay sticky bottom-0 flex items-center justify-end gap-2 px-6 py-4">
                <Button type="button" intent="outline" isDisabled={isActionDisabled} onPress={handleRequestSaveAsSegment} name="save-as-segment">
                    {t("saveAsSegment")}
                </Button>
                <Button type="button" intent="primary" isDisabled={isActionDisabled} onPress={handleApply} name="apply">
                    {t("apply")}
                </Button>
            </div>

            <ConfirmDialog
                isOpen={pendingLeave !== undefined}
                title={tSegmentForm("leaveConfirm.title")}
                description={tSegmentForm("leaveConfirm.description")}
                confirmLabel={tSegmentForm("leaveConfirm.leave")}
                cancelLabel={tSegmentForm("leaveConfirm.stay")}
                confirmIntent="danger"
                onConfirm={handleLeave}
                onCancel={handleStay}
                confirmName="leave"
            />
        </div>
    );
});
